import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import { useUpdateLot, useDeleteLot } from '../hooks/useAuctions';
import { useQueryClient } from '@tanstack/react-query';
import { certificationServices } from '../../../utils/api';

export default function LotFormModal({ isOpen, onClose, lot, auctionId }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutate: updateLot, isLoading: isUpdating } = useUpdateLot();
  const { mutate: deleteLot, isLoading: isDeleting } = useDeleteLot();
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [services, setServices] = useState([]);
  const [formData, setFormData] = useState({
    lot_number: '',
    country: '',
    year: '',
    denomination: '',
    variety: '',
    certification_service_id: '',
    certification_number: '',
    pcgs_number: '',
    grade: '',
    cac_sticker: 'none',
    metal: '',
    composition: '',
    weight: '',
    diameter: '',
    edge: '',
    mint_mark: '',
    mintage: '',
    population: '',
    pop_higher: '',
    price_guide_value: '',
    assayer: '',
    minor_variety: '',
    die_variety: '',
    reference_number: '',
    max_price: '',
    winning_bid: '',
    opening_bid: '',
    status: '',
    notes: ''
  });

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const servicesData = await certificationServices.getAll();
        setServices(servicesData);
      } catch (error) {
        console.error('Failed to fetch certification services:', error);
      }
    };
    fetchServices();
  }, []);

  useEffect(() => {
    if (lot) {
      setFormData({
        lot_number: lot.lot_number || '',
        country: lot.country || '',
        year: lot.year || '',
        denomination: lot.denomination || '',
        variety: lot.variety || '',
        certification_service_id: lot.certification_service_id?.toString() || '',
        certification_number: lot.certification_number || '',
        pcgs_number: lot.pcgs_number || '',
        grade: lot.grade || '',
        cac_sticker: lot.cac_sticker || 'none',
        metal: lot.metal || '',
        composition: lot.composition?.toString() || '',
        weight: lot.weight?.toString() || '',
        diameter: lot.diameter?.toString() || '',
        edge: lot.edge || '',
        mint_mark: lot.mint_mark || '',
        mintage: lot.mintage || '',
        population: lot.population?.toString() || '',
        pop_higher: lot.pop_higher?.toString() || '',
      price_guide_value: lot.price_guide_value?.toString() || '',
      assayer: lot.assayer || '',
      minor_variety: lot.minor_variety || '',
      die_variety: lot.die_variety || '',
      reference_number: lot.reference_number || '',
      max_price: lot.max_price?.toString() || '',
      winning_bid: lot.winning_bid?.toString() || '',
      opening_bid: lot.opening_bid?.toString() || '',
        status: lot.status || '',
        notes: lot.notes || ''
      });
    }
  }, [lot]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    const lotData = {
      ...formData,
      composition: formData.composition === '' ? null : parseFloat(formData.composition),
      weight: formData.weight === '' ? null : parseFloat(formData.weight),
      diameter: formData.diameter === '' ? null : parseFloat(formData.diameter),
      population: formData.population === '' ? null : parseInt(formData.population),
      pop_higher: formData.pop_higher === '' ? null : parseInt(formData.pop_higher),
      price_guide_value: formData.price_guide_value === '' ? null : parseFloat(formData.price_guide_value),
      max_price: formData.max_price === '' ? null : parseFloat(formData.max_price),
      winning_bid: formData.winning_bid === '' ? null : parseFloat(formData.winning_bid),
      opening_bid: formData.opening_bid === '' ? null : parseFloat(formData.opening_bid),
      status: formData.status || null,
      certification_service_id: formData.certification_service_id || null,
      cac_sticker: formData.cac_sticker || 'none' // Ensure CAC sticker value is explicitly set
    };

    updateLot(
      { auctionId, lotId: lot.id, lot: lotData },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['auctions'] });
          await queryClient.refetchQueries({ queryKey: ['auctions'] });
          onClose();
        }
      }
    );
  };

  const handleDelete = () => {
    setIsDeleteConfirmOpen(false);
    onClose();
    navigate(`/auctions/${auctionId}`);
    deleteLot(
      { auctionId, lotId: lot.id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['auctions', auctionId] });
          queryClient.invalidateQueries({ queryKey: ['auctions'] });
        },
      }
    );
  };

  return (
    <>
      <Transition.Root show={isOpen} as={React.Fragment}>
        <Dialog as="div" className="relative z-10" onClose={onClose}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6">
                  <div>
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                      Edit Lot
                    </Dialog.Title>
                    <form onSubmit={handleSubmit} className="mt-4 space-y-8">
                      {/* Basic Information */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-4">Basic Information</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="lot_number" className="block text-sm font-medium text-gray-700">
                              Lot #
                            </label>
                            <input
                              type="text"
                              id="lot_number"
                              required
                              value={formData.lot_number}
                              onChange={(e) => setFormData(prev => ({ ...prev, lot_number: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>

                          <div>
                            <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                              Country
                            </label>
                            <input
                              type="text"
                              id="country"
                              required
                              value={formData.country}
                              onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>

                          <div>
                            <label htmlFor="year" className="block text-sm font-medium text-gray-700">
                              Year
                            </label>
                            <input
                              type="text"
                              id="year"
                              required
                              value={formData.year}
                              onChange={(e) => setFormData(prev => ({ ...prev, year: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>

                          <div>
                            <label htmlFor="mint_mark" className="block text-sm font-medium text-gray-700">
                              Mint Mark
                            </label>
                            <input
                              type="text"
                              id="mint_mark"
                              value={formData.mint_mark}
                              onChange={(e) => setFormData(prev => ({ ...prev, mint_mark: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>

                          <div>
                            <label htmlFor="denomination" className="block text-sm font-medium text-gray-700">
                              Denomination
                            </label>
                            <input
                              type="text"
                              id="denomination"
                              required
                              value={formData.denomination}
                              onChange={(e) => setFormData(prev => ({ ...prev, denomination: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Certification Information */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-4">Certification Information</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="certification_service_id" className="block text-sm font-medium text-gray-700">
                              TPG
                            </label>
                            <select
                              id="certification_service_id"
                              value={formData.certification_service_id}
                              onChange={(e) => setFormData(prev => ({ ...prev, certification_service_id: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            >
                              <option value="">Select TPG</option>
                              {services.map(service => (
                                <option key={service.id} value={service.id}>
                                  {service.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label htmlFor="certification_number" className="block text-sm font-medium text-gray-700">
                              Cert #
                            </label>
                            <input
                              type="text"
                              id="certification_number"
                              value={formData.certification_number}
                              onChange={(e) => setFormData(prev => ({ ...prev, certification_number: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>

                          <div>
                            <label htmlFor="pcgs_number" className="block text-sm font-medium text-gray-700">
                              PCGS #
                            </label>
                            <input
                              type="text"
                              id="pcgs_number"
                              value={formData.pcgs_number}
                              onChange={(e) => setFormData(prev => ({ ...prev, pcgs_number: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>

                          <div>
                            <label htmlFor="grade" className="block text-sm font-medium text-gray-700">
                              Grade
                            </label>
                            <input
                              type="text"
                              id="grade"
                              value={formData.grade}
                              onChange={(e) => setFormData(prev => ({ ...prev, grade: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>

                          <div>
                            <label htmlFor="cac_sticker" className="block text-sm font-medium text-gray-700">
                              CAC
                            </label>
                            <select
                              id="cac_sticker"
                              value={formData.cac_sticker}
                              onChange={(e) => setFormData(prev => ({ ...prev, cac_sticker: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            >
                              <option value="none">None</option>
                              <option value="green">Green</option>
                              <option value="gold">Gold</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Physical Attributes */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-4">Physical Attributes</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="metal" className="block text-sm font-medium text-gray-700">
                              Metal
                            </label>
                            <input
                              type="text"
                              id="metal"
                              value={formData.metal}
                              onChange={(e) => setFormData(prev => ({ ...prev, metal: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>

                          <div>
                            <label htmlFor="composition" className="block text-sm font-medium text-gray-700">
                              Composition %
                            </label>
                            <input
                              type="number"
                              id="composition"
                              step="0.1"
                              value={formData.composition}
                              onChange={(e) => setFormData(prev => ({ ...prev, composition: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>

                          <div>
                            <label htmlFor="weight" className="block text-sm font-medium text-gray-700">
                              Weight (g)
                            </label>
                            <input
                              type="number"
                              id="weight"
                              step="0.001"
                              value={formData.weight}
                              onChange={(e) => setFormData(prev => ({ ...prev, weight: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>

                          <div>
                            <label htmlFor="diameter" className="block text-sm font-medium text-gray-700">
                              Diameter (mm)
                            </label>
                            <input
                              type="number"
                              id="diameter"
                              step="0.01"
                              value={formData.diameter}
                              onChange={(e) => setFormData(prev => ({ ...prev, diameter: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>

                          <div>
                            <label htmlFor="edge" className="block text-sm font-medium text-gray-700">
                              Edge
                            </label>
                            <input
                              type="text"
                              id="edge"
                              value={formData.edge}
                              onChange={(e) => setFormData(prev => ({ ...prev, edge: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>

                        </div>
                      </div>

                      {/* Population Data */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-4">Population Data</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="mintage" className="block text-sm font-medium text-gray-700">
                              Mintage
                            </label>
                            <input
                              type="text"
                              id="mintage"
                              value={formData.mintage}
                              onChange={(e) => setFormData(prev => ({ ...prev, mintage: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>

                          <div>
                            <label htmlFor="population" className="block text-sm font-medium text-gray-700">
                              Population
                            </label>
                            <input
                              type="number"
                              id="population"
                              value={formData.population}
                              onChange={(e) => setFormData(prev => ({ ...prev, population: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>

                          <div>
                            <label htmlFor="pop_higher" className="block text-sm font-medium text-gray-700">
                              Pop Higher
                            </label>
                            <input
                              type="number"
                              id="pop_higher"
                              value={formData.pop_higher}
                              onChange={(e) => setFormData(prev => ({ ...prev, pop_higher: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Variety Information */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-4">Variety Information</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="variety" className="block text-sm font-medium text-gray-700">
                              Variety
                            </label>
                            <input
                              type="text"
                              id="variety"
                              value={formData.variety}
                              onChange={(e) => setFormData(prev => ({ ...prev, variety: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>

                          <div>
                            <label htmlFor="assayer" className="block text-sm font-medium text-gray-700">
                              Assayer
                            </label>
                            <input
                              type="text"
                              id="assayer"
                              value={formData.assayer}
                              onChange={(e) => setFormData(prev => ({ ...prev, assayer: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>

                          <div>
                            <label htmlFor="minor_variety" className="block text-sm font-medium text-gray-700">
                              Minor Variety
                            </label>
                            <input
                              type="text"
                              id="minor_variety"
                              value={formData.minor_variety}
                              onChange={(e) => setFormData(prev => ({ ...prev, minor_variety: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>

                          <div>
                            <label htmlFor="die_variety" className="block text-sm font-medium text-gray-700">
                              Die Variety
                            </label>
                            <input
                              type="text"
                              id="die_variety"
                              value={formData.die_variety}
                              onChange={(e) => setFormData(prev => ({ ...prev, die_variety: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>

                          <div>
                            <label htmlFor="reference_number" className="block text-sm font-medium text-gray-700">
                              Reference #
                            </label>
                            <input
                              type="text"
                              id="reference_number"
                              value={formData.reference_number}
                              onChange={(e) => setFormData(prev => ({ ...prev, reference_number: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Price Information */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-4">Price Information</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="opening_bid" className="block text-sm font-medium text-gray-700">
                              Opening Bid
                            </label>
                            <input
                              type="number"
                              id="opening_bid"
                              step="0.01"
                              value={formData.opening_bid}
                              onChange={(e) => setFormData(prev => ({ ...prev, opening_bid: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>

                          <div>
                            <label htmlFor="price_guide_value" className="block text-sm font-medium text-gray-700">
                              Price Guide Value
                            </label>
                            <input
                              type="number"
                              id="price_guide_value"
                              step="0.01"
                              value={formData.price_guide_value}
                              onChange={(e) => setFormData(prev => ({ ...prev, price_guide_value: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>

                          <div>
                            <label htmlFor="max_price" className="block text-sm font-medium text-gray-700">
                              Max Price (in your currency)
                            </label>
                            <input
                              type="number"
                              id="max_price"
                              step="0.01"
                              value={formData.max_price}
                              onChange={(e) => setFormData(prev => ({ ...prev, max_price: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>

                          <div>
                            <label htmlFor="winning_bid" className="block text-sm font-medium text-gray-700">
                              Hammer Price
                            </label>
                            <input
                              type="number"
                              id="winning_bid"
                              step="0.01"
                              value={formData.winning_bid}
                              onChange={(e) => setFormData(prev => ({ ...prev, winning_bid: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>

                          <div>
                            <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                              Status
                            </label>
                            <select
                              id="status"
                              value={formData.status}
                              onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            >
                              <option value="">Watching</option>
                              <option value="won">Won</option>
                              <option value="lost">Lost</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Notes */}
                      <div>
                        <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                          Notes
                        </label>
                        <textarea
                          id="notes"
                          rows={3}
                          value={formData.notes}
                          onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>

                      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-3 sm:gap-3">
                        <button
                          type="submit"
                          disabled={isUpdating}
                          className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-3 disabled:opacity-50"
                        >
                          {isUpdating ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setIsDeleteConfirmOpen(true)}
                          className="mt-3 inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:col-start-2 sm:mt-0"
                        >
                          Delete
                        </button>
                        <button
                          type="button"
                          onClick={onClose}
                          className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Delete Confirmation Dialog */}
      <Transition.Root show={isDeleteConfirmOpen} as={React.Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsDeleteConfirmOpen(false)}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                      </svg>
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                        Delete Lot
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Are you sure you want to delete this lot? This action cannot be undone.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      disabled={isDeleting}
                      className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto disabled:bg-red-400"
                      onClick={handleDelete}
                    >
                      {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      onClick={() => setIsDeleteConfirmOpen(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}
